import React from 'react'

import {
  CheckboxProps,
  DropdownProps,
  Form,
  Grid,
  Segment,
  Header,
  InputOnChangeData,
  Message,
} from 'semantic-ui-react'
import { Typography } from '@mui/material'

import { IProject } from '../../../api/Data'
import { maxLength } from '../../../config/constants'

import CustomLabel from '../../../components/common/custom-label/CustomLabel'

const CollectTabSuccessPage = ({ project, validationError, handleChange }: IProps) => {
  const {
    successTextDescription,
    successCTAText,
    successCTALink,
    successShowSubmissionReference = true,
    successAllowReceiptDownload = true,
    successRedirectMode = 'custom_url',
    successRedirectDelaySeconds = 15,
    enableContest,
  } = project

  const updateSettings = (event: React.SyntheticEvent, projectChanges: Partial<IProject>) =>
    handleChange(event, { name: 'successPageSettings', projectChanges } as any)

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    const checked = Boolean(data.checked)

    if (data.name === 'successShowSubmissionReference') {
      updateSettings(event, {
        successShowSubmissionReference: checked,
        ...(!checked && { successAllowReceiptDownload: false }),
      })
      return
    }

    updateSettings(event, { [String(data.name)]: checked })
  }

  const handleRedirectModeChange = (event: React.SyntheticEvent, data: DropdownProps) =>
    updateSettings(event, { successRedirectMode: data.value as IProject['successRedirectMode'] })

  const handleRedirectDelayChange = (event: React.SyntheticEvent, data: DropdownProps) =>
    updateSettings(event, { successRedirectDelaySeconds: Number(data.value) as 10 | 15 | 30 })

  return (
    <>
      <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
        Thank You Page Builder
        <Header.Subheader>
          Design a warm thank you message paired with a smooth redirect. Keep the momentum going with a personalized
          post-submission touch.
        </Header.Subheader>
      </Header>

      <Segment>
        <Typography variant="h5" fontWeight="bold">
          Success Page
        </Typography>

        <Typography variant="body1" my={1}>
          After completing a submission, contributors will see this page before choosing what to do next. You can show a
          traceable submission reference and optionally redirect them after 10, 15 or 30 seconds.
        </Typography>

        <Form>
          <Grid stretched padded="vertically">
            <Grid.Column width={16}>
              {/* successTextDescription  */}
              <Form.Field required>
                <CustomLabel>
                  <CustomLabel.Text text="Write a thank you message to you contributors" />
                  <CustomLabel.Popup popupContent="Thanks your contributors and let them know what is the next step."></CustomLabel.Popup>
                  <CustomLabel.WordCount
                    word={successTextDescription}
                    limit={maxLength.successTextDescription}
                  ></CustomLabel.WordCount>
                </CustomLabel>

                <Form.Input
                  placeholder="Type here..."
                  name="successTextDescription"
                  maxLength={maxLength.successTextDescription}
                  value={successTextDescription || ''}
                  onChange={handleChange}
                  error={
                    validationError === 'successTextDescription' && {
                      content: 'A thank you message needs to be entered',
                      pointing: 'above',
                    }
                  }
                />
              </Form.Field>
              {/* successCTAText  */}
              <Form.Field required>
                <CustomLabel>
                  <CustomLabel.Text text="Add a call to action" />
                  <CustomLabel.Popup popupContent="Redirect your contributors to your place of interest." />
                  <CustomLabel.WordCount word={successCTAText} limit={maxLength.successCTAText}></CustomLabel.WordCount>
                </CustomLabel>

                <Form.Input
                  placeholder="Type here..."
                  name="successCTAText"
                  maxLength={maxLength.successCTAText}
                  value={successCTAText || ''}
                  onChange={handleChange}
                  error={
                    validationError === 'successCTAText' && {
                      content: 'A text needs to be entered for the CTA button',
                      pointing: 'above',
                    }
                  }
                />
              </Form.Field>
              {/* successCTALink  */}
              <Form.Field required>
                <CustomLabel>
                  <CustomLabel.Text text="Redirect link for your call to action" />
                  <CustomLabel.Popup popupContent="Redirect your contributors to your place of interest." />
                  <CustomLabel.WordCount word={successCTALink} limit={maxLength.successCTALink}></CustomLabel.WordCount>
                </CustomLabel>

                <Form.Input
                  placeholder="https://www.mydomain.com"
                  name="successCTALink"
                  maxLength={maxLength.successCTALink}
                  value={successCTALink || ''}
                  onChange={handleChange}
                  error={
                    validationError === 'successCTALink' && {
                      content: `A valid link needs to be entered`,
                      pointing: 'above',
                    }
                  }
                />
              </Form.Field>
              <Header as="h3">Submission receipt</Header>
              <Form.Checkbox
                name="successShowSubmissionReference"
                label="Show the submission reference"
                checked={successShowSubmissionReference}
                onChange={handleCheckboxChange}
              />
              <Form.Checkbox
                name="successAllowReceiptDownload"
                label="Allow contributors to download a receipt"
                checked={successShowSubmissionReference && successAllowReceiptDownload}
                disabled={!successShowSubmissionReference}
                onChange={handleCheckboxChange}
              />

              <Header as="h3">After submission</Header>
              <Form.Select
                label="Automatic redirect"
                name="successRedirectMode"
                value={successRedirectMode}
                options={[
                  { key: 'none', text: 'Do not redirect', value: 'none' },
                  { key: 'custom_url', text: 'Redirect to the call-to-action link', value: 'custom_url' },
                  {
                    key: 'public_gallery',
                    text: enableContest
                      ? 'Redirect to the public gallery'
                      : 'Redirect to the public gallery (enable it first)',
                    value: 'public_gallery',
                    disabled: !enableContest,
                  },
                ]}
                onChange={handleRedirectModeChange}
              />
              {successRedirectMode !== 'none' && (
                <Form.Select
                  label="Redirect after"
                  name="successRedirectDelaySeconds"
                  value={successRedirectDelaySeconds}
                  options={[10, 15, 30].map((seconds) => ({
                    key: seconds,
                    text: `${seconds} seconds`,
                    value: seconds,
                  }))}
                  onChange={handleRedirectDelayChange}
                />
              )}
              {successRedirectMode === 'public_gallery' && !enableContest && (
                <Message warning content="Enable Public Gallery in Project Settings before using this redirect." />
              )}
            </Grid.Column>
          </Grid>
        </Form>
      </Segment>
    </>
  )
}

interface IProps {
  project: IProject
  validationError: string | null
  handleChange: (e: React.SyntheticEvent, data: InputOnChangeData & { projectChanges?: Partial<IProject> }) => void
}

export default CollectTabSuccessPage
